import { TriangleAlert } from 'lucide-react'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

type BoundaryProps = {
  /** what failed, for the fallback and the console log */
  label: string
  children: ReactNode
  /** called on Retry before the boundary re-renders its children (e.g. refetch) */
  onRetry?: () => void
  /** when any of these change, a failed boundary resets by itself */
  resetKeys?: unknown[]
  /** 'section' — compact row; 'page' — fills the route outlet */
  scope?: 'section' | 'page'
}

type BoundaryState = { error: Error | null }

const changed = (a: unknown[] = [], b: unknown[] = []) =>
  a.length !== b.length || a.some((v, i) => !Object.is(v, b[i]))

class Boundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[${this.props.label}] render failed`, error, info.componentStack)
  }

  componentDidUpdate(prev: BoundaryProps) {
    if (this.state.error && changed(prev.resetKeys, this.props.resetKeys)) this.setState({ error: null })
  }

  retry = () => {
    this.props.onRetry?.()
    this.setState({ error: null })
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    const page = this.props.scope === 'page'
    return (
      <div
        role="alert"
        className={page
          ? 'm-4 flex items-start gap-3 rounded-md border border-line bg-pane px-4 py-3'
          : 'flex items-start gap-3 rounded-md border border-line bg-pane px-3 py-2.5'}
      >
        <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-danger" />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] text-foreground">
            {page ? 'This page failed to render' : 'This section failed to render'}
            <span className="text-text-3"> · {this.props.label}</span>
          </div>
          <div className="mt-0.5 truncate font-mono text-[11px] text-text-3" title={error.message}>
            {error.message}
          </div>
        </div>
        <button
          type="button"
          onClick={this.retry}
          className="shrink-0 rounded-md border border-line-strong bg-surface-raised px-2.5 py-1 text-[12px] text-foreground transition-colors hover:bg-surface-active"
        >
          Retry
        </button>
      </div>
    )
  }
}

/** Error boundary for one section/panel: the rest of the page keeps working. */
export function SectionBoundary(props: Omit<BoundaryProps, 'scope'>) {
  return <Boundary {...props} scope="section" />
}

/** Route-level boundary: resets on navigation, so a failed page doesn't stick. */
export function PageBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  return (
    // resetKeys, not key: a key would remount every page on each sub-route change and drop its state
    <Boundary label={pathname} resetKeys={[pathname]} scope="page">
      {children}
    </Boundary>
  )
}
